import { useAutoscroll } from '#pplx/frontend/common/hooks/useAutoscroll.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
const SCROLL_IDLE_MS = 150;
const DEFAULT_AUTOSCROLL_THRESHOLD_PX = 300;
export function useScroll(ref) {
    const scrollingRef = useRef(false);
    const nextAutoscrollRequestIdRef = useRef(0);
    const [autoscrollRequests, setAutoscrollRequests] = useState([]);
    const autoscrollOpts = useMemo(() => {
        if (autoscrollRequests.length === 0)
            return undefined;
        const thresholdPx = Math.max(...autoscrollRequests.map(({ opts }) => opts?.thresholdPx ?? DEFAULT_AUTOSCROLL_THRESHOLD_PX));
        const behavior = autoscrollRequests.findLast(({ opts }) => opts?.behavior)?.opts?.behavior;
        return { thresholdPx, behavior };
    }, [autoscrollRequests]);
    useAutoscroll(autoscrollRequests.length > 0, { element: ref, ...autoscrollOpts });
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        let timer;
        const onScroll = () => {
            scrollingRef.current = true;
            clearTimeout(timer);
            timer = setTimeout(() => {
                scrollingRef.current = false;
            }, SCROLL_IDLE_MS);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            el.removeEventListener('scroll', onScroll);
            clearTimeout(timer);
            scrollingRef.current = false;
        };
    }, [ref]);
    const scrollTo = useCallback((pos, opts) => {
        const el = ref.current;
        if (!el)
            return;
        const b = opts?.behavior ?? 'smooth';
        window.requestAnimationFrame(() => {
            const top = pos === 'top' ? 0 : pos === 'bottom' ? el.scrollHeight : pos;
            el.scrollTo({ top, behavior: b });
        });
    }, [ref]);
    const autoscroll = useCallback((opts) => {
        const id = ++nextAutoscrollRequestIdRef.current;
        setAutoscrollRequests((prev) => [...prev, { id, opts }]);
        return () => {
            setAutoscrollRequests((prev) => prev.filter((request) => request.id !== id));
        };
    }, []);
    const isScrolling = useCallback(() => scrollingRef.current, []);
    const isScrollable = useCallback(() => {
        const el = ref.current;
        if (!el)
            return false;
        // 5px offset to account for sub-pixel rendering differences
        return Math.abs(el.scrollHeight - el.clientHeight) > 5;
    }, [ref]);
    const dist = useCallback((pos) => {
        const el = ref.current;
        if (!el)
            return 0;
        const target = pos === 'top' ? 0 : pos === 'bottom' ? el.scrollHeight - el.clientHeight : pos;
        return Math.abs(el.scrollTop - target);
    }, [ref]);
    return useMemo(() => ({ scrollTo, autoscroll, isScrolling, isScrollable, dist }), [scrollTo, autoscroll, isScrolling, isScrollable, dist]);
}
//# sourceMappingURL=useScroll.js.map